import { cn } from "@devhop/ui";
import { dashboardLayout } from "../lib/dashboard-layout";

export type DashboardStatMetric = {
  label: string;
  value: string | number;
  tone?: "default" | "success" | "warning" | "danger" | "muted";
};

const toneValueClass: Record<
  NonNullable<DashboardStatMetric["tone"]>,
  string
> = {
  default: "text-foreground",
  success: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  danger: "text-red-600 dark:text-red-400",
  muted: "text-muted-foreground",
};

type DashboardStatMetricGridProps = {
  metrics: DashboardStatMetric[];
  loading?: boolean;
  columns?: 2 | 3;
};

export function DashboardStatMetricGrid({
  metrics,
  loading = false,
  columns = 2,
}: DashboardStatMetricGridProps) {
  if (loading) {
    return (
      <div
        className={cn(
          "grid gap-2",
          columns === 3 ? "grid-cols-3" : "grid-cols-2",
        )}
      >
        {Array.from({ length: columns === 3 ? 3 : 4 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "animate-pulse rounded-lg bg-muted/50",
              dashboardLayout.metricPad,
            )}
          >
            <div className="mb-2 h-2.5 w-12 rounded bg-muted" />
            <div className="h-6 w-10 rounded bg-muted" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid gap-2",
        columns === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2",
      )}
    >
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className={cn(
            "rounded-lg border border-border/50 bg-muted/30",
            dashboardLayout.metricPad,
          )}
        >
          <p className="text-muted-foreground text-[11px] leading-tight">
            {metric.label}
          </p>
          <p
            className={cn(
              "mt-1 font-bold text-xl tabular-nums leading-none tracking-tight",
              toneValueClass[metric.tone ?? "default"],
            )}
          >
            {typeof metric.value === "number"
              ? metric.value.toLocaleString("lo-LA")
              : metric.value}
          </p>
        </div>
      ))}
    </div>
  );
}
